import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearAllFilters, selectFilters, removeFilter } from '../reducers/filtersReducer';
import useFilterTagProcessor from './useFilterTagProcessor';
import useFilterOptions from './useFilterOptions';
import {
  filterClearIndividual,
  filterClearAll,
  filterCurrentState,
} from '../utils/analyticsEventListeners';
import { INTIAL_FILTERS } from '../constants/initialFilters';
import { selectZipCode } from '../reducers/zipCodeReducer';
import { getShouldShowRemoveAllFilters } from '../utils/filterTagsHelpers';

interface UseFilterTagsReturn {
  isCaMarket: boolean;
  confirmedFilters: ReturnType<typeof selectFilters>['confirmedFilters'];
  renderStringFilters: ReturnType<typeof useFilterTagProcessor>['renderStringFilters'];
  filterCount: number;
  shouldShowRemoveAllFilters: boolean;
  resetAllFilters: () => void;
  handleRemoveFilter: (filterType: string, filter: string) => void;
}

const useFilterTags = (): UseFilterTagsReturn => {
  const dispatch = useDispatch();
  const { listOfAllFilters } = useFilterOptions();
  const { confirmedFilters } = useSelector(selectFilters);
  const { market } = useSelector(selectZipCode);
  const isCaMarket = market === 'CA';

  const { renderStringFilters, filterCount } = useFilterTagProcessor(
    confirmedFilters,
    listOfAllFilters,
    isCaMarket,
  );

  const shouldShowRemoveAllFilters = getShouldShowRemoveAllFilters(
    renderStringFilters,
    isCaMarket,
    filterCount,
  );

  const resetAllFilters = useCallback(() => {
    filterClearAll(confirmedFilters);
    dispatch(clearAllFilters());
    filterCurrentState(INTIAL_FILTERS);
  }, [confirmedFilters, dispatch]);

  const handleRemoveFilter = useCallback(
    (filterType: string, filter: string) => {
      filterClearIndividual(confirmedFilters, `${filterType}.${filter}`);
      dispatch(removeFilter({ filterType, filterValue: filter }));
    },
    [confirmedFilters, dispatch],
  );

  return {
    isCaMarket,
    confirmedFilters,
    renderStringFilters,
    filterCount,
    shouldShowRemoveAllFilters,
    resetAllFilters,
    handleRemoveFilter,
  };
};

export default useFilterTags;
